import React, { useState, useEffect, useCallback } from 'react';
import { useMode } from '../contexts/ModeContext';
import { useTranslation } from 'react-i18next';
import { API_ORIGIN, apiBaseUrl } from '../config/runtime';
import { getToken } from '../utils/auth';
import {
  ClipboardList, CreditCard, CheckCircle2, Clock,
  TrendingUp, Wallet, Star, MessageSquare
} from 'lucide-react';
import ReviewForm from '../components/ReviewForm';
import ReviewList from '../components/ReviewList';

interface Order {
  id: number;
  room_id: number;
  project_id: number | null;
  client_id: number;
  freelancer_id: number;
  price: number;
  status: 'pending' | 'in_progress' | 'pending_approval' | 'completed' | 'cancelled' | 'disputed';
  start_date: string;
  end_date: string;
  project_title: string | null;
  client_nickname: string;
  freelancer_nickname: string;
  created_at: string;
  completion_requested_at: string | null;
}

interface Transaction {
  id: number;
  type: string;
  amount: number;
  description: string;
  status: string;
  created_at: string;
}

interface Stats {
  totalCount: number;
  totalAmount: number;
  totalFees: number;
  successRate: number;
}

/* ── 상태 배지 ── */
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const { t } = useTranslation();
  const map: Record<string, { label: string; bg: string; color: string }> = {
    negotiating:      { label: t('chat.negotiating'),    bg: '#F5F5F5',  color: '#888' },
    in_progress:      { label: t('projects.statusInProgress'),    bg: '#EEF2FF',  color: '#4F46E5' },
    pending_approval: { label: t('transactions.statusPendingApproval'), bg: '#FFF7ED',  color: '#D97706' },
    completed:        { label: t('transactions.statusCompleted'),      bg: '#ECFDF5',  color: '#059669' },
    cancelled:        { label: t('transactions.statusCancelled'),    bg: '#F5F5F5',  color: '#999' },
    disputed:         { label: t('transactions.statusDisputed'),    bg: '#FEF2F2',  color: '#DC2626' },
  };
  const s = map[status] || { label: status, bg: '#F5F5F5', color: '#999' };
  return (
    <span style={{
      padding: '3px 10px', borderRadius: 100, fontSize: 12, fontWeight: 600,
      background: s.bg, color: s.color,
    }}>{s.label}</span>
  );
};

const INDIGO = '#4F46E5';

const TransactionsPage: React.FC = () => {
  const { isClientMode, isFreelancerMode } = useMode();
  const { t, i18n } = useTranslation();
  const [orders, setOrders] = useState<Order[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [stats, setStats] = useState<Stats>({ totalCount: 0, totalAmount: 0, totalFees: 0, successRate: 0 });
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'orders' | 'transactions'>('orders');

  // 리뷰 관련 상태
  const [reviewTarget, setReviewTarget] = useState<{ orderId: number; tradeId?: number } | null>(null);
  const [canReviewMap, setCanReviewMap] = useState<Record<number, boolean>>({});
  const [expandedReviews, setExpandedReviews] = useState<Record<number, boolean>>({});

  const fetchOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const token = getToken();
      if (!token) return;
      const role = isClientMode ? 'client' : 'freelancer';
      const res = await fetch(`${API_ORIGIN}/api/contract/orders?role=${role}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setOrders(data.data || []);
        // 완료된 거래의 리뷰 가능 여부 확인
        const completed = (data.data || []).filter((o: Order) => o.status === 'completed');
        for (const order of completed) {
          checkCanReview(order.id);
        }
      }
    } catch (err) { console.error('주문 목록 조회 오류:', err); }
    finally { setOrdersLoading(false); }
  }, [isClientMode]);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      if (!token) return;
      const res = await fetch(`${API_ORIGIN}/api/dashboard/transactions?page=1&limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setTransactions(data.data || []);
        setStats(data.stats || { totalCount: 0, totalAmount: 0, totalFees: 0, successRate: 0 });
      }
    } catch (err) { console.error('거래내역 조회 오류:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  const checkCanReview = async (orderId: number) => {
    try {
      const token = getToken();
      const res = await fetch(apiBaseUrl(`/reviews/can-review/${orderId}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setCanReviewMap(prev => ({ ...prev, [orderId]: data.canReview }));
      }
    } catch { /* silent */ }
  };

  const handleAction = async (orderId: number, action: string) => {
    const messages: Record<string, string> = {
      complete: t('transactions.confirmComplete'),
      approve: t('transactions.confirmApprove'),
    };
    if (!window.confirm(messages[action] || t('transactions.confirmProceed'))) return;

    try {
      const token = getToken();
      const endpoints: Record<string, string> = {
        complete: `/api/contract/order/${orderId}/request-completion`,
        approve:  `/api/contract/order/${orderId}/complete`,
      };
      const res = await fetch(`${API_ORIGIN}${endpoints[action]}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.success) {
        fetchOrders();
      } else {
        alert(data.message || t('transactions.requestFailed'));
      }
    } catch { alert(t('transactions.errorOccurred')); }
  };

  const formatDate = (d: string) => d ? new Date(d).toLocaleDateString(i18n.language === 'ko' ? 'ko-KR' : 'en-US') : '-';
  const formatAmount = (n: number) => `₩${Number(n).toLocaleString()}`;

  const card: React.CSSProperties = {
    background: '#fff', borderRadius: 16,
    border: '1px solid #F0F0F0', padding: '20px 24px', marginBottom: 12,
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '28px 20px' }}>
      <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111', letterSpacing: '-0.5px', marginBottom: 4 }}>
        {t('transactions.title')}
      </h1>
      <p style={{ fontSize: 14, color: '#888', marginBottom: 24 }}>
        {isClientMode ? t('transactions.subtitleClient') : t('transactions.subtitleFreelancer')}
      </p>

      {/* 탭 */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, background: '#F5F5F5', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {[
          { key: 'orders', label: t('transactions.tabOrders', { n: orders.length }), icon: <ClipboardList size={14} /> },
          { key: 'transactions', label: t('transactions.tabTransactions', { n: stats.totalCount }), icon: <CreditCard size={14} /> },
        ].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key as any)} style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '8px 16px', border: 'none', borderRadius: 8, cursor: 'pointer',
            fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
            background: activeTab === t.key ? '#fff' : 'transparent',
            color: activeTab === t.key ? INDIGO : '#888',
            boxShadow: activeTab === t.key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
            transition: 'all 0.15s',
          }}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {/* ── 프로젝트 탭 ── */}
      {activeTab === 'orders' && (
        <>
          {ordersLoading ? (
            <div style={{ textAlign: 'center', padding: 48, color: '#BBB', fontSize: 14 }}>{t('common.loading')}</div>
          ) : orders.length === 0 ? (
            <div style={{ ...card, textAlign: 'center', padding: 48, color: '#BBB' }}>
              <ClipboardList size={40} color="#DDD" style={{ marginBottom: 12 }} />
              <div style={{ fontSize: 15, fontWeight: 600, color: '#888' }}>
                {isClientMode ? t('transactions.noProjectsClient') : t('transactions.noProjectsFreelancer')}
              </div>
            </div>
          ) : (
            orders.map(order => (
              <div key={order.id} style={card}>
                {/* 헤더 */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <StatusBadge status={order.status} />
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#111' }}>
                      {order.project_title || t('transactions.project')}
                    </span>
                  </div>
                  <span style={{ fontSize: 16, fontWeight: 800, color: INDIGO, letterSpacing: '-0.3px' }}>
                    {formatAmount(order.price)}
                  </span>
                </div>

                {/* 상세 */}
                <div style={{ fontSize: 13, color: '#888', display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 14 }}>
                  <span>{isClientMode ? t('transactions.expertName', { name: order.freelancer_nickname }) : t('transactions.clientName', { name: order.client_nickname })}</span>
                  <span>{formatDate(order.start_date)} ~ {formatDate(order.end_date)}</span>
                </div>

                {/* 액션 버튼 */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  {/* 전문가: 작업완료 */}
                  {isFreelancerMode && order.status === 'in_progress' && (
                    <button onClick={() => handleAction(order.id, 'complete')} style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      padding: '8px 16px', background: '#ECFDF5', color: '#059669',
                      border: '1px solid #A7F3D0', borderRadius: 8, fontSize: 13,
                      fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                    }}>
                      <CheckCircle2 size={14} />{t('transactions.notifyComplete')}
                    </button>
                  )}

                  {/* 의뢰자: 승인 */}
                  {isClientMode && order.status === 'pending_approval' && (
                    <button onClick={() => handleAction(order.id, 'approve')} style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      padding: '8px 16px', background: '#EEF2FF', color: INDIGO,
                      border: `1px solid #C7D2FE`, borderRadius: 8, fontSize: 13,
                      fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                    }}>
                      <CheckCircle2 size={14} />{t('transactions.approveComplete')}
                    </button>
                  )}

                  {/* 완료 후 리뷰 작성 */}
                  {order.status === 'completed' && canReviewMap[order.id] && (
                    <button onClick={() => setReviewTarget({ orderId: order.id })} style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      padding: '8px 16px', background: '#FFFBEB', color: '#D97706',
                      border: '1px solid #FDE68A', borderRadius: 8, fontSize: 13,
                      fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                    }}>
                      <Star size={13} />{t('transactions.writeReview')}
                    </button>
                  )}

                  {/* 리뷰 보기 */}
                  {order.status === 'completed' && (
                    <button onClick={() => setExpandedReviews(prev => ({ ...prev, [order.id]: !prev[order.id] }))} style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      padding: '8px 14px', background: 'transparent', color: '#BBB',
                      border: '1px solid #F0F0F0', borderRadius: 8, fontSize: 13,
                      fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
                    }}>
                      <MessageSquare size={13} />
                      {expandedReviews[order.id] ? t('transactions.closeReview') : t('transactions.viewReview')}
                    </button>
                  )}

                  {order.status === 'in_progress' && isClientMode && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#AAA' }}>
                      <Clock size={13} />{t('projects.statusInProgress')}
                    </span>
                  )}
                </div>

                {/* 리뷰 작성 폼 */}
                {reviewTarget?.orderId === order.id && (
                  <div style={{ marginTop: 16 }}>
                    <ReviewForm
                      tradeId={order.id}
                      onSuccess={() => {
                        setReviewTarget(null);
                        setCanReviewMap(prev => ({ ...prev, [order.id]: false }));
                      }}
                      onCancel={() => setReviewTarget(null)}
                    />
                  </div>
                )}

                {/* 리뷰 목록 */}
                {expandedReviews[order.id] && (
                  <div style={{ marginTop: 16, padding: '16px', background: '#FAFAFA', borderRadius: 10 }}>
                    <ReviewList tradeId={order.id} compact />
                  </div>
                )}
              </div>
            ))
          )}
        </>
      )}

      {/* ── 거래내역 탭 ── */}
      {activeTab === 'transactions' && (
        <>
          {/* 통계 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
            {[
              { icon: <ClipboardList size={18} />, label: t('transactions.statTotal'), value: t('transactions.countN', { n: stats.totalCount }), accent: INDIGO },
              { icon: <Wallet size={18} />, label: t('transactions.statAmount'), value: formatAmount(stats.totalAmount), accent: '#10B981' },
              { icon: <TrendingUp size={18} />, label: t('transactions.statFees'), value: formatAmount(stats.totalFees), accent: '#F59E0B' },
            ].map((s, i) => (
              <div key={i} style={{ ...card, marginBottom: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: s.accent + '15', color: s.accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {s.icon}
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#999' }}>{s.label}</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#111', letterSpacing: '-0.3px' }}>{s.value}</div>
                </div>
              </div>
            ))}
          </div>

          {/* 리스트 */}
          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111', marginBottom: 16 }}>{t('transactions.historyTitle')}</h3>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 32, color: '#BBB', fontSize: 14 }}>{t('common.loading')}</div>
            ) : transactions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32, color: '#BBB', fontSize: 14 }}>{t('transactions.noHistory')}</div>
            ) : (
              transactions.map(tx => {
                const isIncome = ['deposit', 'earning'].includes(tx.type);
                return (
                  <div key={tx.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '13px 0', borderBottom: '1px solid #F5F5F5',
                  }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500, color: '#111', marginBottom: 3 }}>
                        {tx.description}
                      </div>
                      <div style={{ fontSize: 12, color: '#BBB' }}>{formatDate(tx.created_at)}</div>
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: isIncome ? '#059669' : '#EF4444' }}>
                      {isIncome ? '+' : '-'}{formatAmount(tx.amount)}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TransactionsPage;
